// src/pos-app/surfaces/waiter/OrderDrawer.js
//
// F12a-N Task W4 — the native order-taking drawer (design contract:
// docs/mockups/f12a-n/waiter-tables.html, "Order drawer" section — slide-in
// right over the panel, both New/Append modes). Replaces
// components/pos/WaiterOrderDrawer.js's UI on this surface; the legacy
// component is UNTOUCHED (still backs TableServicePage / WaiterStation's
// SURFACES.waiter='legacy' fallback).
//
// Decision (documented per the task brief): REUSE the submit engine,
// REBUILD the UI. Every guard/branch/tracking-call in ./lib/drawerFlow.js is
// a faithful, cut-and-paste extraction of WaiterOrderDrawer.js's
// handleSubmit + its tracking helpers (see that file's header for the exact
// source-range table) — this component only supplies the UI shell and wires
// user actions into that engine's `ctx` contract. AddonSelectionModal is
// REUSED wholesale, zero edits.
//
// Hook composition (per the brief: "instantiated per drawer-open, or
// surface-owned — document the choice; do not fight cashier's SellSurface
// instances"): this component owns its OWN useProductCatalog +
// useOrderBuilder instances, exactly mirroring how the legacy
// WaiterOrderDrawer composed them internally (its own header, lines 78-107).
// TablesSurface always mounts exactly one <OrderDrawer/> (display-toggled by
// `isOpen`, never unmounted while a table session is in progress) — so
// "per drawer-open" and "surface-owned" collapse to the same thing here:
// one long-lived instance per waiter surface, matching the legacy
// component's own catalogLoadedRef pattern (load the catalog once, reseed
// only the cart on each open). This is a wholly separate hook tree from
// cashier's SellSurface (which owns its own useProductCatalog/
// useOrderBuilder pair) — nothing here can collide with it; each station
// mounts independently.
//
// Portal rationale (same trap CartPanel.js's header documents, one level
// higher): WaiterStation.js renders this surface's ancestor with
// `style={glass({...})}` on `.wst-tables` (AuroraBackground's `glass()`
// helper sets `backdropFilter` inline), and TablesSurface's own `.wtb-panel`
// sets a second `backdrop-filter` closer in. Either one becomes the
// containing block for `position:fixed` descendants (same CSS rule as
// `transform`/`filter`), so an un-portaled `position:fixed` drawer would
// size/position itself against a 40%-width glass column instead of the
// viewport. Scrim + drawer are portaled to `document.body` — the one node
// with no filter/backdrop-filter/transform ancestor — restoring true
// viewport-fixed positioning.
//
// Business logic is REUSED, never forked: catalog data from
// useProductCatalog, cart state from useOrderBuilder (selectedProducts,
// notes, addon flow via handleProductWithAddons -> the REUSED
// AddonSelectionModal, quantity/notes/remove handlers), totals from the
// SAME calculateTotal() the legacy drawer used (simple subtotal/service/tax/
// total, no discount — waiter orders never discount), and the entire submit
// engine via ./lib/drawerFlow.submitOrder.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../../context/AuthContext';
import { useLanguage, getProductName } from '../../../context/LanguageContext';
import useBusinessDay from '../../../hooks/pos/useBusinessDay';
import useProductCatalog from '../../../hooks/pos/useProductCatalog';
import useOrderBuilder from '../../../hooks/pos/useOrderBuilder';
import AddonSelectionModal from '../../../components/pos/modals/AddonSelectionModal';
import { modifierService } from '../../../services/api';
import { getDisplayName, submitOrder } from './lib/drawerFlow';

// Tokens only (--c-* theme vars, light + dark). Logical properties keep the
// drawer RTL-correct; all tap targets are ≥44px. Class names mirror the
// mockup's own (.drawer, .chip, .prow, .line, …) with a wod- prefix.
// W4 fix — z-index band: this drawer/scrim USED to sit at 1300/1301,
// above OperatorLockScreen's z:70 (both portal to document.body, so they
// share one real stacking context — see that file's header). An order
// left mid-entry when the station locked kept the drawer mounted over the
// PIN pad, and its full-viewport scrim ate every tap meant for it — the
// same class of bug as task-n8 Bug B, just with a different overlay.
// Native overlay band (all document.body-portaled, ascending):
//   OrdersPanelNative 45  <  CartPanel sheet 50  <  this (55/56)  <
//   TopBarExtras 60  <  OperatorLockScreen 70  <  StationSettingsSheet 80.
// 55/56 keeps this below TopBarExtras' 60 (matching CartPanel's existing
// convention of sitting under the top-bar's "More" menu — WaiterStation
// doesn't mount TopBarExtras today, but StationTopBar is the shared shell
// component CashierStation already hangs it off, so reserving the slot
// below 60 here keeps the band's ordering meaningful if/when it does) and
// — the actual bug fix — below the lock screen's 70, so a lock always
// wins. TablesSurface additionally closes the drawer outright on lock
// (belt-and-braces: no overlay should persist through a shift handover).
const CSS = `
.wod-scrim{position:fixed;inset:0;z-index:55;background:rgba(9,13,22,.45);
  opacity:0;pointer-events:none;transition:opacity .18s;
  -webkit-backdrop-filter:blur(2px);backdrop-filter:blur(2px);}
.wod-scrim.open{opacity:1;pointer-events:auto;}
.wod-drawer{position:fixed;z-index:56;top:0;inset-inline-end:0;bottom:0;width:min(430px,94vw);
  background:var(--c-surface);border-inline-start:1px solid var(--c-border);
  box-shadow:-12px 0 40px rgba(2,8,23,.22);
  transform:translateX(103%);transition:transform .22s cubic-bezier(.32,.72,.33,1);
  display:flex;flex-direction:column;}
[dir="rtl"] .wod-drawer{transform:translateX(-103%);}
.wod-drawer.open{transform:none;}

.wod-head{flex:none;display:flex;align-items:center;gap:10px;padding:14px 16px 10px;
  border-bottom:1px solid var(--c-border);}
.wod-head-main{flex:1;min-width:0;}
.wod-l1{font-size:15px;font-weight:800;letter-spacing:-.2px;color:var(--c-ink);
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.wod-l2{font-size:11px;color:var(--c-muted);font-weight:600;}
.wod-mode{flex:none;font-size:9.5px;font-weight:800;letter-spacing:.8px;padding:4px 10px;border-radius:999px;
  color:var(--c-green);background:var(--c-greenLight);border:1px solid var(--c-greenBorder);text-transform:uppercase;}
.wod-mode.append{color:var(--c-amber);background:var(--c-amberLight);border-color:var(--c-amberBorder);}
.wod-x{position:relative;flex:none;width:36px;height:36px;border-radius:10px;border:1px solid var(--c-border);
  background:var(--c-faint);color:var(--c-muted);cursor:pointer;display:flex;align-items:center;justify-content:center;}
.wod-x:hover{color:var(--c-red);background:var(--c-redLight);border-color:var(--c-redBorder);}

.wod-chips{flex:none;display:flex;gap:7px;overflow-x:auto;padding:10px 16px 8px;scrollbar-width:none;}
.wod-chips::-webkit-scrollbar{display:none;}
.wod-chip{position:relative;flex:none;min-height:34px;padding:0 14px;border-radius:999px;
  font-family:inherit;font-size:12px;font-weight:700;color:var(--c-slate);
  background:var(--c-surface);border:1px solid var(--c-border);cursor:pointer;}
.wod-chip::after{content:"";position:absolute;inset-block:-6px;inset-inline:0;}
.wod-chip.active{background:var(--c-ink);color:var(--c-bg);border-color:var(--c-ink);}

.wod-catalog{flex:1;min-height:0;overflow-y:auto;padding:2px 16px 8px;}
.wod-prow{display:flex;align-items:center;gap:10px;padding:9px 0;border-bottom:1px solid var(--c-border);}
.wod-pn{flex:1;min-width:0;}
.wod-pname{font-size:13px;font-weight:700;color:var(--c-ink);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.wod-pstock{font-size:10.5px;font-weight:700;color:var(--c-red);}
.wod-pp{font-size:12.5px;font-weight:700;color:var(--c-muted);font-variant-numeric:tabular-nums;}
.wod-padd{position:relative;flex:none;width:32px;height:32px;border-radius:10px;border:1px solid var(--c-greenBorder);
  background:var(--c-greenLight);color:var(--c-green);cursor:pointer;display:flex;align-items:center;justify-content:center;}
.wod-padd::after{content:"";position:absolute;inset:-6px;}
.wod-padd:hover:not(:disabled){background:var(--c-green);color:#fff;}
.wod-padd:disabled{opacity:.4;cursor:not-allowed;}
.wod-empty{text-align:center;padding:30px 10px;font-size:13px;font-weight:600;color:var(--c-muted);}

.wod-error{margin:8px 16px 0;padding:8px 12px;border-radius:9px;background:var(--c-redLight);
  border:1px solid var(--c-redBorder);color:var(--c-red);font-size:12px;font-weight:600;}

.wod-order{flex:none;max-height:37%;overflow-y:auto;padding:8px 16px 4px;border-top:1px solid var(--c-border);
  background:var(--c-faint);}
.wod-ogroup{font-size:10.5px;font-weight:800;text-transform:uppercase;letter-spacing:1px;color:var(--c-muted);
  margin:4px 0 6px;}
.wod-oempty{text-align:center;padding:16px 6px;font-size:12.5px;font-weight:600;color:var(--c-muted);}
.wod-line{display:flex;gap:10px;padding:8px 0;border-bottom:1px solid var(--c-border);align-items:flex-start;}
.wod-line.sent{opacity:.6;}
.wod-line.isnew{border-inline-start:3px solid var(--c-green);padding-inline-start:9px;margin-inline-start:-12px;
  background:linear-gradient(90deg, var(--c-greenLight), transparent 70%);border-radius:0 8px 8px 0;}
.wod-lmain{flex:1;min-width:0;}
.wod-lname{font-size:13px;font-weight:700;color:var(--c-ink);display:flex;align-items:center;gap:7px;flex-wrap:wrap;}
.wod-sentmark{display:inline-flex;align-items:center;gap:3px;font-size:9px;font-weight:800;letter-spacing:.5px;
  color:var(--c-green);background:var(--c-greenLight);border:1px solid var(--c-greenBorder);border-radius:6px;padding:1px 6px;
  text-transform:uppercase;}
.wod-newmark{font-size:9px;font-weight:800;letter-spacing:.5px;color:var(--c-green);background:var(--c-greenLight);
  border:1px solid var(--c-greenBorder);border-radius:6px;padding:1px 6px;text-transform:uppercase;}
.wod-addon{display:inline-flex;align-items:center;gap:4px;margin-block-start:4px;font-size:10.5px;font-weight:700;
  color:var(--c-green);background:var(--c-greenLight);border:1px solid var(--c-greenBorder);border-radius:999px;padding:2px 8px;}
.wod-note{display:block;width:100%;margin-block-start:4px;font-family:inherit;font-size:11px;font-style:italic;
  color:var(--c-muted);background:transparent;border:none;border-bottom:1px dashed var(--c-border);padding:2px 0;outline:none;}
.wod-note:focus{border-bottom-color:var(--c-green);color:var(--c-ink);}
.wod-lside{display:flex;flex-direction:column;align-items:flex-end;gap:6px;}
.wod-ltotal{font-size:13px;font-weight:800;color:var(--c-ink);font-variant-numeric:tabular-nums;}
.wod-stepper{display:inline-flex;align-items:center;gap:2px;background:var(--c-surface);border:1px solid var(--c-border);
  border-radius:999px;padding:2px;}
.wod-stepper button{position:relative;width:25px;height:25px;border-radius:50%;border:none;cursor:pointer;
  display:flex;align-items:center;justify-content:center;background:transparent;color:var(--c-slate);}
.wod-stepper button::after{content:"";position:absolute;inset:-8px;}
.wod-stepper button:hover:not(:disabled){background:var(--c-greenLight);color:var(--c-green);}
.wod-stepper button:disabled{opacity:.35;cursor:not-allowed;}
.wod-qty{min-width:20px;text-align:center;font-size:12.5px;font-weight:800;color:var(--c-ink);}
.wod-lremove{position:relative;background:none;border:none;cursor:pointer;color:var(--c-red);font-size:10.5px;
  font-weight:700;font-family:inherit;padding:2px 0;}
.wod-lremove::after{content:"";position:absolute;inset:-8px;}
.wod-onote{width:100%;box-sizing:border-box;min-height:38px;border:1px dashed var(--c-border);border-radius:10px;
  padding:8px 12px;background:var(--c-surface);font-family:inherit;font-size:12px;color:var(--c-ink);outline:none;
  margin:8px 0 4px;resize:none;}
.wod-onote:focus{border-color:var(--c-green);border-style:solid;}

.wod-totals{flex:none;padding:9px 16px 12px;border-top:1px solid var(--c-border);}
.wod-trow{display:flex;align-items:center;justify-content:space-between;padding:2.5px 0;font-size:12.5px;
  color:var(--c-slate);font-weight:600;}
.wod-trow .money{font-variant-numeric:tabular-nums;}
.wod-trow.grand{padding-top:7px;margin-top:4px;border-top:1px solid var(--c-border);font-size:16px;font-weight:800;
  color:var(--c-ink);}
.wod-send{width:100%;min-height:52px;margin-top:9px;border-radius:14px;border:none;cursor:pointer;
  background:var(--c-green);color:#fff;font-family:inherit;font-size:15px;font-weight:800;letter-spacing:.2px;
  box-shadow:0 8px 20px color-mix(in srgb, var(--c-green) 38%, transparent);
  display:flex;align-items:center;justify-content:center;gap:9px;}
.wod-send:hover:not(:disabled){filter:brightness(.96);}
.wod-send:disabled{opacity:.5;cursor:not-allowed;box-shadow:none;}
.wod-send.append{background:var(--c-amber);box-shadow:0 8px 20px color-mix(in srgb, var(--c-amber) 35%, transparent);}
.wod-appendhint{font-size:10.5px;color:var(--c-muted);font-weight:600;text-align:center;margin-top:6px;}
.wod-guard{display:flex;align-items:center;justify-content:center;gap:6px;margin-top:7px;font-size:10.5px;
  font-weight:700;color:var(--c-muted);}
.wod-guard i{width:7px;height:7px;border-radius:50%;background:var(--c-green);}
`;

const CloseIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
        <path d="M18 6 6 18M6 6l12 12" />
    </svg>
);
const PlusIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
        <path d="M12 5v14M5 12h14" />
    </svg>
);
const MinusGlyph = (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
        <path d="M5 12h14" />
    </svg>
);
const SendIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" />
    </svg>
);
const CheckIcon = (
    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M20 6 9 17l-5-5" />
    </svg>
);

// Legacy WaiterOrderDrawer.js:28-34 (stockFor) — a UI-only safety net (out
// of stock disables the catalog row's add button); not part of the submit
// engine, so it stays here rather than in lib/drawerFlow.js.
const stockFor = (p, bulkCalculatedStock) => {
    if (!p) return 0;
    if (p.stock_calculation_type === 'auto') {
        return bulkCalculatedStock[p.product_code]?.available_stock ?? 0;
    }
    return p.quantity === -9999 ? Infinity : p.quantity;
};

// Same matching key utils/OrderHelpers.calculateNewItemsForTracking uses
// (product_code + sorted addon codes) — kept in lockstep deliberately so
// this line's SENT/NEW display can never drift from what
// updateOrderTrackingRecords actually diffs/patches on submit.
const productSignature = (p) =>
    `${p.product_code}::${(p.add_ons || []).map((a) => a.product_code || a.id).sort().join(',')}`;

export default function OrderDrawer({
    isOpen, onClose, mode = 'new',
    diningOptionName, diningOptionObject, tableNumber,
    occupation, existingOrder, onOrderPlaced,
    onAddonModalOpenChange,
}) {
    const { tenantConfig, username, getCurrency } = useAuth();
    const currency = getCurrency();
    const { t, language, translationDetails } = useLanguage();
    const { isBusinessDay, businessDaySalesDate } = useBusinessDay();

    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const originalProductsRef = useRef([]);
    const catalogLoadedRef = useRef(false);

    const {
        categories, printableCategories,
        selectedCategory, setSelectedCategory,
        bulkCalculatedStock, products, filteredProducts, loadCatalog, refreshBulkStock,
    } = useProductCatalog(tenantConfig);

    const diningOptionObjects = useMemo(
        () => (diningOptionName ? { [diningOptionName]: diningOptionObject } : {}),
        [diningOptionName, diningOptionObject],
    );

    const {
        selectedProducts, setSelectedProducts,
        notes, setNotes,
        addServiceCharge, addTax,
        currentProduct,
        currentProductGroups, activeCatalog, modifierTree,
        toggleModifier, modifierUnmet, modifierFlagUnmet, modifierLineTotal,
        addonModalOpen, setAddonModalOpen,
        calculateTotal,
        handleProductWithAddons,
        addProductWithAddons,
        handleUpdateProductQuantity,
        handleRemoveProduct,
        handleUpdateProductNotes,
        setModifierCatalog,
    } = useOrderBuilder({
        tenantConfig, selectedDiningOption: diningOptionName,
        diningOptionObjects, bulkCalculatedStock, products, setError, t,
    });

    // Load catalog once (ref-guarded, legacy 112-115), and (re)seed the cart
    // whenever the drawer opens for a (possibly different) table/order —
    // legacy WaiterOrderDrawer.js:110-132, verbatim seeding semantics: append
    // mode deep-clones existingOrder.products TWICE (once into the live cart,
    // once into originalProductsRef) so later edits to selectedProducts can
    // never mutate the pre-edit snapshot drawerFlow.submitOrder diffs against.
    useEffect(() => {
        if (!isOpen) return;
        if (!catalogLoadedRef.current) {
            loadCatalog();
            // Feed the modifier catalog once too (Task 17) — without this the
            // builder's modifierCatalog never leaves its empty useState
            // default and every product falls back to the legacy flat
            // add-on path. Routes online/offline the same way loadCatalog
            // does (modifierService.getCatalog() branches internally).
            modifierService.getCatalog()
                .then(setModifierCatalog)
                .catch((err) => console.error('Failed to load modifier catalog:', err));
            catalogLoadedRef.current = true;
        }
        setError('');
        setSelectedCategory(null);
        if (mode === 'append' && existingOrder?.products) {
            const seed = JSON.parse(JSON.stringify(existingOrder.products));
            originalProductsRef.current = JSON.parse(JSON.stringify(existingOrder.products));
            setSelectedProducts(seed);
            setNotes(existingOrder.notes || '');
        } else {
            originalProductsRef.current = [];
            setSelectedProducts([]);
            setNotes('');
        }
        // Intentionally limited deps — re-seed only when the drawer opens or
        // the target order changes, same rule the legacy drawer applies.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, mode, tableNumber, existingOrder?.order_id]);

    // Escape-chain centralization (F12a-N Task W5, see TablesSurface's
    // header for the full priority chain): this drawer no longer runs its
    // own window keydown listener (legacy WaiterOrderDrawer.js:135-140's
    // equivalent). It reports `addonModalOpen` up via the optional
    // `onAddonModalOpenChange` prop so the parent chain can gate around it —
    // the REUSED AddonSelectionModal (components/pos/modals/, zero edits)
    // is Modal.js-based and keeps its OWN Escape self-dismissal (same
    // precedent SellSurface.js's Escape chain documents for its reused
    // Modal.js-backed overlays), so the parent must NOT also close the
    // drawer on the same keypress while the addon modal is open on top.
    useEffect(() => {
        if (onAddonModalOpenChange) onAddonModalOpenChange(addonModalOpen);
    }, [addonModalOpen, onAddonModalOpenChange]);

    const totals = calculateTotal();
    const itemCount = selectedProducts.reduce((s, p) => s + (parseInt(p.quantity) || 1), 0);
    const isAppend = mode === 'append';
    const staffName = getDisplayName(username);

    // Catalog rows for the active chip — "All" reuses the full product list
    // (sorted), a category chip reuses the hook's own filtered list, same
    // derivation CatalogBrowse.js uses minus the search term (this drawer
    // has no search field per the mockup).
    const rowProducts = useMemo(() => {
        if (selectedCategory) return filteredProducts;
        return [...products].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [selectedCategory, filteredProducts, products]);

    const productLabel = (p) => getProductName(p, language, translationDetails) || p.name;

    // Append-mode sent/new line classification — same matching key
    // utils/OrderHelpers.calculateNewItemsForTracking uses, so this display
    // can't drift from what actually gets a fresh KDS ticket on submit.
    const sentQtyFor = (product) => {
        if (!isAppend) return 0;
        const sig = productSignature(product);
        const match = originalProductsRef.current.find((op) => productSignature(op) === sig);
        return match ? (parseInt(match.quantity) || 0) : 0;
    };

    const handleSubmit = () => submitOrder({
        mode, isBusinessDay, businessDaySalesDate,
        diningOptionName, tableNumber, selectedProducts, notes, totals,
        addServiceCharge, addTax,
        occupation, existingOrder, originalProducts: originalProductsRef.current,
        tenantConfig, username, products, printableCategories,
        t, setError, setSubmitting,
        refreshBulkStock, onOrderPlaced, onClose,
    });

    const drawerBody = (
        <>
            <div className={`wod-scrim${isOpen ? ' open' : ''}`} onClick={onClose} />
            <div
                className={`wod-drawer${isOpen ? ' open' : ''}`}
                role="dialog" aria-modal="true"
                aria-label={t('order')}
            >
                <style>{CSS}</style>

                <div className="wod-head">
                    <div className="wod-head-main">
                        <div className="wod-l1">
                            {t('table')} {tableNumber} · {isAppend ? t('add_items') : t('new_order')}
                        </div>
                        <div className="wod-l2">
                            {diningOptionName}
                            {occupation?.party_size ? ` · ${occupation.party_size} ${t('guests')}` : ''}
                            {` · ${staffName}`}
                        </div>
                    </div>
                    <span className={`wod-mode${isAppend ? ' append' : ''}`}>
                        {isAppend ? t('update_order') : t('new')}
                    </span>
                    <button type="button" className="wod-x" aria-label={t('close')} onClick={onClose}>
                        {CloseIcon}
                    </button>
                </div>

                {categories.length > 0 && (
                    <div className="wod-chips" role="group" aria-label={t('products')}>
                        <button
                            type="button"
                            className={`wod-chip${!selectedCategory ? ' active' : ''}`}
                            onClick={() => setSelectedCategory(null)}
                        >
                            {t('all')}
                        </button>
                        {categories.map((cat) => (
                            <button
                                key={cat}
                                type="button"
                                className={`wod-chip${selectedCategory === cat ? ' active' : ''}`}
                                onClick={() => setSelectedCategory(cat)}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                )}

                <div className="wod-catalog">
                    {rowProducts.length === 0 ? (
                        <div className="wod-empty">{t('no_products_in_category')}</div>
                    ) : rowProducts.map((p) => {
                        const stock = stockFor(p, bulkCalculatedStock);
                        const out = stock !== Infinity && stock <= 0;
                        return (
                            <div className="wod-prow" key={p.product_code || p.id}>
                                <span className="wod-pn">
                                    <div className="wod-pname">{productLabel(p)}</div>
                                    {out && <div className="wod-pstock">{t('out_of_stock')}</div>}
                                </span>
                                <span className="wod-pp money">{currency}{(parseFloat(p.selling_price) || 0).toFixed(2)}</span>
                                <button
                                    type="button"
                                    className="wod-padd"
                                    disabled={out}
                                    aria-label={`${t('add_to_order')} ${productLabel(p)}`}
                                    onClick={() => handleProductWithAddons(p)}
                                >
                                    {PlusIcon}
                                </button>
                            </div>
                        );
                    })}
                </div>

                {error && <div className="wod-error" role="alert">{error}</div>}

                <div className="wod-order">
                    <div className="wod-ogroup">{t('order')} — {t('table')} {tableNumber}</div>
                    {selectedProducts.length === 0 ? (
                        <div className="wod-oempty">{t('tap_products_to_add')}</div>
                    ) : selectedProducts.map((item) => {
                        const key = item.uniqueId || item.product_code;
                        const sentQty = sentQtyFor(item);
                        const currentQty = parseInt(item.quantity) || 1;
                        const fullySent = isAppend && sentQty > 0 && currentQty <= sentQty;
                        const partialNew = isAppend && sentQty > 0 && currentQty > sentQty;
                        const wholeNew = isAppend && sentQty === 0;
                        const linePrice = (parseFloat(item.selling_price) || 0) + (parseFloat(item.add_on_total) || 0);
                        return (
                            <div className={`wod-line${fullySent ? ' sent' : ''}${wholeNew || partialNew ? ' isnew' : ''}`} key={key}>
                                <div className="wod-lmain">
                                    <div className="wod-lname">
                                        {productLabel(item)}
                                        {(fullySent || partialNew) && (
                                            <span className="wod-sentmark">{CheckIcon}{t('sent')}</span>
                                        )}
                                        {wholeNew && <span className="wod-newmark">{t('new')}</span>}
                                        {partialNew && <span className="wod-newmark">+{currentQty - sentQty} {t('new')}</span>}
                                    </div>
                                    {item.add_ons && item.add_ons.length > 0 && (
                                        item.add_ons.map((a, idx) => (
                                            <span className="wod-addon" key={a?.product_code || a?.id || idx}>
                                                + {a?.name} <span className="money">{currency}{(parseFloat(a?.price) || 0).toFixed(2)}</span>
                                            </span>
                                        ))
                                    )}
                                    {!fullySent && (
                                        <input
                                            className="wod-note"
                                            value={item.notes || ''}
                                            onChange={(e) => handleUpdateProductNotes(key, e.target.value)}
                                            placeholder={t('add_note')}
                                            aria-label={t('add_note')}
                                        />
                                    )}
                                    {fullySent && item.notes && <div className="wod-note">{item.notes}</div>}
                                </div>
                                <div className="wod-lside">
                                    <span className="wod-ltotal money">{currency}{(linePrice * currentQty).toFixed(2)}</span>
                                    {fullySent ? (
                                        <span className="wod-stepper" aria-hidden="true">
                                            <span className="wod-qty">×{currentQty}</span>
                                        </span>
                                    ) : (
                                        <>
                                            <span className="wod-stepper">
                                                <button
                                                    type="button"
                                                    aria-label={t('decrease_quantity')}
                                                    disabled={currentQty <= 1}
                                                    onClick={() => handleUpdateProductQuantity(key, currentQty - 1)}
                                                >{MinusGlyph}</button>
                                                <span className="wod-qty">{currentQty}</span>
                                                <button
                                                    type="button"
                                                    aria-label={t('increase_quantity')}
                                                    onClick={() => handleUpdateProductQuantity(key, currentQty + 1)}
                                                >{PlusIcon}</button>
                                            </span>
                                            <button
                                                type="button"
                                                className="wod-lremove"
                                                onClick={() => handleRemoveProduct(key)}
                                            >
                                                {t('remove')}
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    <textarea
                        className="wod-onote"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder={t('add_order_notes')}
                        aria-label={t('add_order_notes')}
                    />
                </div>

                <div className="wod-totals">
                    <div className="wod-trow"><span>{t('subtotal')}</span><span className="money">{currency}{totals.subtotal.toFixed(2)}</span></div>
                    {addServiceCharge && (
                        <div className="wod-trow"><span>{t('service_charge')}</span><span className="money">{currency}{totals.serviceCharge.toFixed(2)}</span></div>
                    )}
                    {addTax && totals.tax > 0 && (
                        <div className="wod-trow"><span>{t('tax')}</span><span className="money">{currency}{totals.tax.toFixed(2)}</span></div>
                    )}
                    <div className="wod-trow grand"><span>{t('total')}</span><span className="money">{currency}{totals.total.toFixed(2)}</span></div>
                    <button
                        type="button"
                        className={`wod-send${isAppend ? ' append' : ''}`}
                        disabled={submitting || selectedProducts.length === 0}
                        onClick={handleSubmit}
                    >
                        {SendIcon}
                        {submitting ? t('sending') : (isAppend ? t('update_order') : t('send_to_kitchen'))}
                        {itemCount > 0 && !submitting && <span className="money">· {itemCount} {itemCount === 1 ? t('item') : t('items')}</span>}
                    </button>
                    {isAppend && <div className="wod-appendhint">{t('order_drawer_append_hint')}</div>}
                    <div className="wod-guard"><i />{t('business_day_active')}</div>
                </div>
            </div>

            <AddonSelectionModal
                isOpen={addonModalOpen}
                onClose={() => setAddonModalOpen(false)}
                currentProduct={currentProduct}
                groupIds={currentProductGroups}
                catalog={activeCatalog}
                tree={modifierTree}
                onToggle={toggleModifier}
                unmet={modifierUnmet}
                flagUnmet={modifierFlagUnmet}
                lineTotal={modifierLineTotal}
                onAdd={addProductWithAddons}
                currency={currency}
            />
        </>
    );

    return createPortal(drawerBody, document.body);
}
